import { useState, useEffect } from "react";
import type { FC, KeyboardEvent, ChangeEvent } from "react";
import styles from "./CategoryList.module.scss";
import { useNavigate, useSearchParams } from "react-router-dom";

interface CategoryListProps {
  selectedCategory: string | null;
  onSelectCategory: (id: string | null) => void;
  isMenu?: boolean; // режим: true — акордеон, false — двохярусне з flyout
  onClose?: () => void;
}

type CategoryData = Record<string, string>;
type CategoryMap = Record<string, CategoryData>;

const fetchCategories = async (catid?: string): Promise<CategoryData> => {
  const params: Record<string, string> = { type: "category", legid: "1" };
  if (catid !== undefined) params.catid = catid;

  const response = await fetch("https://crm.hixa.eu/external/goodsapi/", {
    method: "POST",
    headers: {
      Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: new URLSearchParams(params),
  });

  const data = await response.json();
  return data.success && data.data ? data.data : {};
};

const CategoryList: FC<CategoryListProps> = ({
  selectedCategory,
  onSelectCategory,
  isMenu = false,
  onClose,
}) => {
  const [categories, setCategories] = useState<CategoryData>({});
  const [subcategories, setSubcategories] = useState<CategoryMap>({});
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null); // для isMenu=true (акордеон)
  const [hoveredParent, setHoveredParent] = useState<string | null>(null); // для isMenu=false (двохярусне)

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("search") || "";
  const [query, setQuery] = useState(initialQuery);

  // ── initial load
  useEffect(() => {
    const loadInitial = async () => {
      const root = await fetchCategories();
      setCategories(root);
    };
    loadInitial();
  }, []);

  // ── якщо це двохярусне меню: підвантажимо підкатегорії для першого батька
  useEffect(() => {
    if (isMenu) return;
    const ids = Object.keys(categories);
    if (!ids.length) return;
    const first = ids[0];
    if (!hoveredParent) {
      setHoveredParent(first);
    }
    if (first && !subcategories[first]) {
      fetchCategories(first).then((subs) =>
        setSubcategories((prev) => ({ ...prev, [first]: subs }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, isMenu]);

  // ── акордеон (режим меню)
  const handleToggle = async (id: string) => {
    if (expandedCategory === id) {
      setExpandedCategory(null);
    } else {
      if (!subcategories[id]) {
        const subs = await fetchCategories(id);
        setSubcategories((prev) => ({ ...prev, [id]: subs }));
      }
      setExpandedCategory(id);
    }
  };

  // ── двохярусне: hover по батьківському
  const handleParentHover = async (id: string) => {
    setHoveredParent(id);
    if (!subcategories[id]) {
      const subs = await fetchCategories(id);
      setSubcategories((prev) => ({ ...prev, [id]: subs }));
    }
  };

  const handleSubClick = (subId: string) => {
    onSelectCategory(subId); // закриваємо лише дочірній список (flyout), головне меню залишається
    setHoveredParent(null);
  };

  const handleSearch = () => {
    const trimmed = query.trim();
    if (trimmed) {
      navigate(`/shop?search=${encodeURIComponent(trimmed)}`);
    }
  };

  const applyBtn = () => {
    handleSearch();
    onClose?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleReset = () => {
    onSelectCategory(null);
    setQuery("");
    navigate("/shop");
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // РЕНДЕР
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={isMenu ? styles.categoryBlockMenu : styles.categoryBlock}>
      <div className={styles.filterBlock}>
        <div className={` textBasic ${styles.filterTitle}`}>Filter</div>
        <div className={styles.searchBar}>
          <input
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Suche Produkt..."
          />
          <button
            className={styles.button}
            aria-label="Suche starten"
            onClick={handleSearch}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z" />
            </svg>
          </button>
        </div>
      </div>
      <div className={`textBasic ${styles.filterTitle}`}>Kategorien</div>

      {/* ── Режим А: isMenu=true (акордеон зі стрілками) ───────────────────── */}
      {isMenu && (
        <ul className={styles.categoryList}>
          {Object.entries(categories).map(([id, name]) => {
            const isExpanded = expandedCategory === id;
            return (
              <li
                key={id}
                className={`${styles.categoryItemWrapper} ${
                  isExpanded ? styles.expanded : styles.collapsed
                }`}
              >
                <div className={styles.categoryItem}>
                  <span
                    onClick={() => {
                      onSelectCategory(id);
                      onClose?.(); // якщо треба автозакривати меню після вибору батька
                    }}
                  >
                    {name}
                  </span>
                  <button
                    className={styles.faqItemButton}
                    onClick={() => handleToggle(id)}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      className={styles.arrowIcon}
                    >
                      <path
                        d="M1.50002 4L6.00002 8L10.5 4"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      />
                    </svg>
                  </button>
                </div>
                {isExpanded && subcategories[id] && (
                  <ul>
                    {Object.entries(subcategories[id]).map(
                      ([subId, subName]) => (
                        <li
                          key={subId}
                          className={`${styles.subItem} ${
                            selectedCategory === subId ? styles.active : ""
                          }`}
                          onClick={() => {
                            onSelectCategory(subId);
                            onClose?.(); // автозакриття меню після вибору підкатегорії
                          }}
                        >
                          {subName}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Режим Б: isMenu=false (двохярусне, діти під батьком) ──────────── */}
      {!isMenu && (
        <ul className={styles.flyoutList}>
          {Object.entries(categories).map(([id, name]) => (
            <li
              key={id}
              className={styles.parentWrapper}
              onMouseEnter={() => handleParentHover(id)}
            >
              <button
                type="button"
                className={styles.parentItem}
                onClick={() => onSelectCategory(id)}
              >
                {name}
              </button>
              {/* flyout показуємо тільки для активного батька */}
              <ul
                className={styles.childFlyout}
                style={{ display: hoveredParent === id ? "block" : "none" }}
              >
                {subcategories[id] ? (
                  Object.entries(subcategories[id]).map(([subId, subName]) => (
                    <li key={subId}>
                      <button
                        type="button"
                        className={`${styles.subItem} ${
                          selectedCategory === subId ? styles.active : ""
                        }`}
                        onClick={() => handleSubClick(subId)}
                      >
                        {subName}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className={styles.loading}>Loading...</li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      )}
      <button className={styles.resetBtn} onClick={handleReset}>
        Reset
      </button>
      {isMenu && (
        <button className={styles.applyBtn} onClick={applyBtn}>
          Apply filter
        </button>
      )}
    </div>
  );
};

export default CategoryList;
